import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { whisperTranscribe } from "@/lib/api";
import { listenOnceEnGB } from "@/lib/browserSpeech";
import { weightedPhraseMatch, type WordMatchStatus } from "@/lib/speechScore";
import { plainTextForTts } from "@/lib/script";
import { MathRichText } from "@/lib/mathRichText";
import type { SpeakSlideData } from "@/types/lesson";

interface SpeakSlideProps {
  slide: SpeakSlideData;
}

const MIC_PERMISSION_MSG =
  "Microphone not detected. Please check your browser permissions.";

/** Stop recording automatically so the learner doesn’t have to tap Stop. */
const AUTO_STOP_RECORD_MS = 18_000;

type SpeakResult =
  | {
      kind: "scored";
      score: number;
      transcript: string;
      details: { word: string; status: WordMatchStatus }[];
      source: "server" | "browser";
    }
  | { kind: "empty_transcript" }
  | { kind: "sr_unavailable"; message: string };

function chipClass(status: WordMatchStatus): string {
  switch (status) {
    case "ok":
      return "bg-green-200 text-green-900 border border-green-300";
    case "partial":
      return "bg-amber-100 text-amber-900 border border-amber-300";
    case "miss":
      return "bg-red-200 text-red-900 border border-red-300";
    default:
      return "bg-slate-200 text-slate-800";
  }
}

export function SpeakSlide({ slide }: SpeakSlideProps) {
  const [recording, setRecording] = useState(false);
  const [result, setResult] = useState<SpeakResult | null>(null);
  const [loading, setLoading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
    };
  }, []);

  const processTranscript = (transcript: string, source: "server" | "browser") => {
    const trimmed = transcript.trim();
    if (!trimmed) {
      setResult({ kind: "empty_transcript" });
      return;
    }
    const { score, details } = weightedPhraseMatch(plainTextForTts(slide.phrase), trimmed);
    setResult({
      kind: "scored",
      score,
      transcript: trimmed,
      details,
      source,
    });
  };

  const tryBrowserSpeechFallback = async () => {
    try {
      const text = await listenOnceEnGB();
      processTranscript(text, "browser");
    } catch (e) {
      const code = e instanceof Error ? e.message : "";
      if (
        code === "NO_SPEECH_RESULT" ||
        code === "no-speech" ||
        code === "aborted"
      ) {
        setResult({ kind: "empty_transcript" });
        return;
      }
      if (code === "NO_SPEECH_API") {
        setResult({
          kind: "sr_unavailable",
          message:
            "Speech recognition isn’t available in this browser. Try Chrome or Edge, or update your browser.",
        });
        return;
      }
      if (code === "not-allowed" || code === "service-not-allowed") {
        setResult({ kind: "sr_unavailable", message: MIC_PERMISSION_MSG });
        return;
      }
      setResult({ kind: "sr_unavailable", message: MIC_PERMISSION_MSG });
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setLoading(true);
        try {
          const w = await whisperTranscribe(blob);

          if (w.success && w.transcript.trim()) {
            processTranscript(w.transcript, "server");
            return;
          }

          if (w.success && !w.transcript.trim()) {
            setResult({ kind: "empty_transcript" });
            return;
          }

          await tryBrowserSpeechFallback();
        } catch {
          await tryBrowserSpeechFallback();
        } finally {
          setLoading(false);
        }
      };
      mr.start(250);
      setRecording(true);
      setResult(null);
      if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = setTimeout(() => {
        autoStopTimerRef.current = null;
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
          setRecording(false);
        }
      }, AUTO_STOP_RECORD_MS);
    } catch {
      setResult({ kind: "sr_unavailable", message: MIC_PERMISSION_MSG });
    }
  };

  const stopRecording = () => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const tryAgain = () => {
    setResult(null);
  };

  return (
    <motion.div
      className="h-full flex flex-col min-h-0 overflow-hidden rounded-lg"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25 }}
    >
      <div
        className="px-4 py-2 rounded-t-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-lg shrink-0"
        style={{ fontFamily: "var(--lp-font-heading)" }}
      >
        <MathRichText text={slide.title} childTheme />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        <div className="text-slate-700 font-medium">
          <MathRichText text={slide.prompt} />
        </div>
        <div className="rounded-lg border-2 border-dashed border-green-400 bg-green-50/50 p-4">
          <div className="text-lg font-semibold text-slate-800 text-center">
            “<MathRichText text={slide.phrase} />”
          </div>
        </div>
        {!result ? (
          <>
            {!recording ? (
              <motion.button
                type="button"
                onClick={startRecording}
                disabled={loading}
                className="w-full py-3 rounded-lg font-semibold text-white bg-green-500 hover:bg-green-600 disabled:opacity-50 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? "Checking…" : "Start Recording"}
              </motion.button>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <motion.div
                  className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                >
                  <span className="text-white text-2xl">🎤</span>
                </motion.div>
                <p className="text-slate-600 text-sm text-center">
                  Recording… stops automatically after ~{Math.round(AUTO_STOP_RECORD_MS / 1000)}s
                </p>
                <button
                  type="button"
                  onClick={stopRecording}
                  className="px-4 py-2 rounded-lg font-medium bg-red-500 text-white hover:bg-red-600"
                >
                  Stop now
                </button>
              </div>
            )}
          </>
        ) : (
          <motion.div className="space-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {result.kind === "scored" && (
              <>
                <p className="font-semibold text-slate-800">Score: {result.score}%</p>
                <p className="text-slate-700">
                  <span className="font-medium">You said:</span> “{result.transcript}”
                </p>
                {result.source === "browser" && (
                  <p className="text-xs text-slate-500">
                    (Recognised in your browser — online speech check wasn’t available for this attempt.)
                  </p>
                )}
                {result.details.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {result.details.map((d, i) => (
                      <span key={i} className={`px-2 py-0.5 rounded text-sm ${chipClass(d.status)}`}>
                        {d.word}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
            {result.kind === "empty_transcript" && (
              <p className="text-slate-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3">
                I couldn&apos;t hear you — try speaking louder 🎤
              </p>
            )}
            {result.kind === "sr_unavailable" && (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {result.message}
              </p>
            )}
            <button
              type="button"
              onClick={tryAgain}
              className="px-4 py-2 rounded-lg font-medium border-2 border-slate-300 hover:bg-slate-100"
            >
              Try Again
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
